import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { verifyPasscode, setStoredPasscode, getStoredPasscode } from "@/lib/api";
import { Lock } from "lucide-react";

const LENGTH = 4;

export default function PasscodePage() {
  const [digits, setDigits] = useState(["", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const inputs = useRef([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (getStoredPasscode()) navigate("/dashboard", { replace: true });
    setTimeout(() => inputs.current[0]?.focus(), 100);
  }, [navigate]);

  const handleChange = (idx, val) => {
    const v = val.replace(/[^0-9]/g, "").slice(0, 1);
    const next = [...digits];
    next[idx] = v;
    setDigits(next);
    setError("");
    if (v && idx < LENGTH - 1) inputs.current[idx + 1]?.focus();
    if (idx === LENGTH - 1 && v) tryVerify(next.join(""));
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !digits[idx] && idx > 0) inputs.current[idx - 1]?.focus();
    if (e.key === "Enter") tryVerify(digits.join(""));
  };

  const handlePaste = (e) => {
    const text = e.clipboardData.getData("text").replace(/[^0-9]/g, "").slice(0, LENGTH);
    if (!text) return;
    e.preventDefault();
    const next = ["", "", "", ""];
    for (let i = 0; i < text.length; i++) next[i] = text[i];
    setDigits(next);
    if (text.length === LENGTH) tryVerify(text);
    else inputs.current[text.length]?.focus();
  };

  const tryVerify = async (code) => {
    if (code.length < LENGTH) return;
    setLoading(true);
    try {
      await verifyPasscode(code);
      setStoredPasscode(code);
      toast.success("Access granted");
      navigate("/dashboard", { replace: true });
    } catch {
      setError("Invalid passcode");
      setDigits(["", "", "", ""]);
      inputs.current[0]?.focus();
      toast.error("Invalid passcode");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-white" data-testid="passcode-page">
      <div className="absolute inset-0 -z-10 grid-lines opacity-60" />
      <div
        className="absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(circle at 25% 15%, rgba(0,122,255,0.08), transparent 40%), radial-gradient(circle at 90% 90%, rgba(0,0,0,0.04), transparent 45%)",
        }}
      />

      <div className="relative z-10 min-h-screen w-full flex items-start justify-start">
        <div className="flex flex-col w-full max-w-2xl px-6 md:px-16 pt-16 md:pt-24">
          <div className="flex items-center gap-3 mb-16">
            <div className="w-8 h-8 border border-black/25 flex items-center justify-center">
              <Lock className="w-4 h-4 text-black/70" />
            </div>
            <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-black/60">
              elevenlabs · convo · export
            </div>
          </div>

          <div className="font-mono-plex text-[11px] uppercase tracking-[0.3em] text-black/50 mb-3">
            &gt; system_access
          </div>
          <h1 className="font-heading text-5xl md:text-6xl font-light tracking-tight text-black mb-2">
            Enter passcode
          </h1>
          <p className="font-body text-sm md:text-base text-black/60 mb-12 max-w-md">
            Four-digit access code required to enter the workspace conversation history dashboard.
          </p>

          <div className="flex items-center gap-3 mb-6" onPaste={handlePaste}>
            {digits.map((d, i) => (
              <input
                key={i}
                data-testid={`passcode-digit-${i}`}
                ref={(el) => (inputs.current[i] = el)}
                inputMode="numeric"
                autoComplete="one-time-code"
                type="password"
                value={d}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                disabled={loading}
                className="w-14 h-16 md:w-20 md:h-24 text-center text-2xl md:text-4xl font-mono-plex bg-white text-black border border-black/25 focus:outline-none focus:border-black focus:ring-1 focus:ring-black/30 caret-transparent"
              />
            ))}
            <span className="terminal-cursor ml-2 hidden sm:inline-block" />
          </div>

          <div className="font-mono-plex text-xs h-5 mb-8">
            {error ? (
              <span data-testid="passcode-error" className="text-[#DC2626]">✗ {error}</span>
            ) : loading ? (
              <span className="text-black/60">verifying…</span>
            ) : (
              <span className="text-black/40">auto-submits when 4 digits entered</span>
            )}
          </div>

          <div className="mt-16 md:mt-24 border-t border-black/10 pt-4">
            <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-black/30">
              internal · single-workspace · read-only-mirror
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
